import React, { useCallback, useEffect, useRef } from 'react';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import useMousePosition from '../../hooks/useMousePosition';
import './Particles.css';

const ParticlesBackground = () => {
  const containerRef = useRef(null);
  const mousePosition = useMousePosition();

  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const particlesLoaded = useCallback((container) => {
    containerRef.current = container;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container && container.canvas && mousePosition.x !== 0 && mousePosition.y !== 0) {
      const canvas = container.canvas.element;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const pos = {
          x: (mousePosition.x - rect.left) * (canvas.width / rect.width),
          y: (mousePosition.y - rect.top) * (canvas.height / rect.height)
        };
        
        // Update interactivity mouse position
        if (container.interactivity && container.interactivity.mouse) {
          container.interactivity.mouse.position = pos;
        }
      }
    }
  }, [mousePosition]);

  useEffect(() => {
    const handleClick = (event) => {
      const container = containerRef.current;
      if (container && container.canvas) {
        const canvas = container.canvas.element;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          const pos = {
            x: (event.clientX - rect.left) * (canvas.width / rect.width),
            y: (event.clientY - rect.top) * (canvas.height / rect.height)
          };
          
          container.interactivity.mouse.clickPosition = pos;
          container.interactivity.mouse.position = pos;
        }
      }
    };

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, []);

  const particlesConfig = {
    fullScreen: {
      enable: false,
      zIndex: 0
    },
    particles: {
      number: {
        value: 80,
        density: {
          enable: true,
          area: 800
        }
      },
      color: {
        value: ['#00494D', '#007A82', '#000']
      },
      shape: {
        type: 'circle'
      },
      opacity: {
        value: 0.6,
        random: true,
        animation: {
          enable: true,
          speed: 1,
          minimumValue: 0.3,
          sync: false
        }
      },
      size: {
        value: 3,
        random: true,
        animation: {
          enable: true,
          speed: 2,
          minimumValue: 0.1,
          sync: false
        }
      },
      links: {
        enable: true,
        distance: 150,
        color: '#00494D',
        opacity: 0.4,
        width: 1
      },
      move: {
        enable: true,
        speed: 1,
        direction: 'none',
        random: false,
        straight: false,
        outModes: {
          default: 'out'
        },
        attract: {
          enable: false,
          rotateX: 600,
          rotateY: 1200
        }
      }
    },
    interactivity: {
      detectsOn: 'window',
      events: {
        onHover: {
          enable: true,
          mode: ['grab', 'bubble']
        },
        onClick: {
          enable: true,
          mode: 'push'
        },
        resize: true
      },
      modes: {
        grab: {
          distance: 200,
          links: {
            opacity: 0.8
          }
        },
        bubble: {
          distance: 200,
          size: 6,
          duration: 0.3,
          opacity: 0.8
        },
        push: {
          quantity: 4
        },
        repulse: {
          distance: 100,
          duration: 0.4
        }
      }
    },
    detectRetina: true
  };

  return (
    <Particles
      id="tsparticles"
      init={particlesInit}
      loaded={particlesLoaded}
      options={particlesConfig}
    />
  );
};

export default ParticlesBackground;
